"use client";

import { Facebook, Linkedin, Mail, Youtube, X, Instagram } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function PinterestIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 11.5c0-2.5 2-4.5 4.5-4.5s4 1.7 4 3.8c0 2.6-1.7 4.7-3.9 4.7-1 0-1.8-.5-2-1.2l-.5 2.1c-.2.9-.9 2-1.4 2.7" />
      <path d="M10 17.5c.5-.5 1-1.5 1.2-2.3" />
    </svg>;
}
function NavigateMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3, 4].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted/40 animate-pulse rounded-md" />
          </li>)}
      </ul>;
  }
  return <ul className="space-y-3 font-default text-sm text-muted-foreground">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="relative inline-block text-muted-foreground hover:text-primary transition-colors after:absolute after:left-0 after:bottom-0 after:h-px after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function CorporateFooter() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const boutiqueLinks = [{
    label: "Data Analytics",
    to: "/DataAnalyticsPage"
  }, {
    label: "Data Automation & Technology",
    to: "/DataAutomationTechnologyPage"
  }, {
    label: "Support",
    to: "/GetInTouchPage"
  }];
  const connectLinks = [{
    label: "LinkedIn",
    to: "#",
    icon: Linkedin,
    link: {
      href: "https://www.linkedin.com/company/infinix-solutions-cyprus",
      newTab: true
    }
  }, {
    label: "X",
    to: "https://x.com/infinix_2024",
    icon: X
  }, {
    label: "Facebook",
    to: "https://facebook.com/InfiniXsolutionsCY",
    icon: Facebook
  }, {
    label: "Instagram",
    to: "https://instagram.com/infinixsolutions_cy",
    icon: Instagram
  }, {
    label: "info@infinix-solutions.org",
    to: "mailto:contact@infinixsolutions.com",
    icon: Mail
  }];
  const legalLinks = [];
  return <section data-section-id="3874" id="corporate-footer" className="text-foreground relative overflow-hidden dark bg-accent">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.15 0 0 0 0 0.15 0 0 0 0 0.1 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      backgroundSize: "240px 240px"
    }} />

      {}
      <Separator className="bg-border" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-24 sm:pt-32 lg:pt-36 pb-12 sm:pb-14 lg:pb-16">
        {}
        <div className="w-full mb-20 sm:mb-28 lg:mb-32">
          <h2 ref={headlineRef} className="font-default font-semibold tracking-tighter leading-[0.95] text-foreground transition-all duration-1000 ease-out lg:pl-[6%]" style={{
          fontSize: "clamp(4rem, 12vw, 9rem)",
          opacity: headlineVisible ? 1 : 0,
          transform: headlineVisible ? "translateY(0)" : "translateY(24px)",
          filter: headlineVisible ? "blur(0)" : "blur(6px)"
        }}>Clear Data. <br />Smarter Decisions. Stronger Trust.</h2>
        </div>

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 sm:gap-10 lg:gap-12">
          {}
          <div data-index={0} className="sm:col-span-2 lg:col-span-1">
            <Link to="/HomePage" className="inline-block mb-6 !h-[60px] !w-[50px]">
              <WvcLogo className="text-primary !bg-transparent !w-[50px] [&_svg]:text-primary [&_svg]:!bg-transparent [&_svg]:!w-[50px]" />
            </Link>
            <p className="font-default text-sm leading-relaxed text-muted-foreground max-w-xs">
              Infinix Solutions architects actionable intelligence through precision data analytics and automated systems.
            </p>
            
          </div>

          {}
          <div data-index={1}>
            <h3 className="font-default text-base font-semibold text-foreground mb-6">
              Navigate
            </h3>
            <MenuProvider menu_id="17">
              <NavigateMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div data-index={2}>
            <h3 className="font-default text-base font-semibold text-foreground mb-6">
              Services
            </h3>
            <ul className="space-y-3 font-default text-sm text-muted-foreground">
              {boutiqueLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="relative inline-block text-muted-foreground hover:text-primary transition-colors after:absolute after:left-0 after:bottom-0 after:h-px after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div data-index={3}>
            <h3 className="font-default text-base font-semibold text-foreground mb-6">
              Connect
            </h3>
            <ul className="space-y-3 font-default text-sm text-muted-foreground">
              {connectLinks.map((item, i) => {
              const Icon = item.icon;
              return <li key={item.label} data-index={i}>
                    <Link to={item.to} className="group inline-flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors">
                      <Icon className="w-4 h-4 stroke-[1.25]" />
                      <span className="relative after:absolute after:left-0 after:bottom-0 after:h-px after:w-0 after:bg-primary after:transition-all after:duration-300 group-hover:after:w-full">
                        {item.label}
                      </span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>
        </div>

        {}
        <Separator className="bg-border mt-20 sm:mt-24 lg:mt-28" />

        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-8">
          <p className="font-default text-[11px] uppercase tracking-[0.22em] text-secondary">
            &copy; 2026 INFINIX SOLUTIONS &mdash; FOR DEFINITE PROBLEMS INFINITE SOLUTIONS
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 font-default text-[11px] uppercase tracking-[0.22em] text-muted-foreground/55">
            {legalLinks.map((item, i) => <li key={item.label} data-index={i}>
                <Link to={item.to} className="hover:text-primary transition-colors text-secondary">
                  {item.label}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}